
import { supabase } from '@/lib/supabase';
import { MarketAnalysisMonitoringService } from './ai-analysis/MarketAnalysisMonitoringService';
import { AIAnalysisDebugger, createDebugContext } from '@/utils/ai-analysis-debug';

export interface AiAnalysisRequest {
  symbol: string;
  market_summary_id: string;
  user_id: string;
  language?: string;
}

export interface AiAnalysisResponse {
  success: boolean;
  session_id?: string;
  message: string;
  current_step: number;
  total_steps: number;
  error?: string;
}

export interface AiAnalysisSession {
  id: string;
  user_id: string;
  symbol: string;
  market_summary_id: string;
  status: 'PENDING' | 'PROCESSING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'RATE_LIMITED';
  current_step: number;
  total_steps: number;
  progress_percentage: number;
  status_message?: string;
  card_results: {
    ai_recommendation: CardResult;
    trends_analysis: CardResult;
    key_levels: CardResult;
    price_targets: CardResult;
    analysis_summary: CardResult;
  };
  error_message?: string;
  retry_after?: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface CardResult {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  data: any;
  timestamp: string | null;
  error?: string;
}

export class AiMarketAnalysisService {
  /**
   * Start AI Market Analysis
   */
  static async startAnalysis(request: AiAnalysisRequest): Promise<AiAnalysisResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('ai-market-analysis', {
        body: request
      });

      if (error) {
        console.error('Error calling AI analysis function:', error);
        return {
          success: false,
          message: error.message || 'Failed to start analysis',
          current_step: 0,
          total_steps: 5,
          error: error.message
        };
      }

      return data;
    } catch (error) {
      console.error('Error in startAnalysis:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
        current_step: 0,
        total_steps: 5,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get analysis session by ID
   */
  static async getAnalysisSession(sessionId: string): Promise<AiAnalysisSession | null> {
    try {
      const { data, error } = await supabase
        .from('ai_analysis_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (error) {
        console.error('Error fetching analysis session:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getAnalysisSession:', error);
      return null;
    }
  }

  /**
   * Get analysis sessions by user
   */
  static async getUserAnalysisSessions(userId: string, limit: number = 20): Promise<AiAnalysisSession[]> {
    try {
      const { data, error } = await supabase
        .from('ai_analysis_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching user analysis sessions:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserAnalysisSessions:', error);
      return [];
    }
  }

  /**
   * Get analysis sessions by symbol
   */
  static async getSymbolAnalysisSessions(symbol: string, limit: number = 10): Promise<AiAnalysisSession[]> {
    try {
      const { data, error } = await supabase
        .from('ai_analysis_sessions')
        .select('*')
        .eq('symbol', symbol)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching symbol analysis sessions:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getSymbolAnalysisSessions:', error);
      return [];
    }
  }

  /**
   * Delete analysis session
   */
  static async deleteAnalysisSession(sessionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('ai_analysis_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in deleteAnalysisSession:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Enhanced poll for analysis progress with monitoring and debugging
   */
  static async pollAnalysisProgress(
    sessionId: string,
    onProgress: (session: AiAnalysisSession) => void,
    onComplete: (session: AiAnalysisSession) => void,
    onError: (error: string) => void,
    intervalMs: number = 2000
  ): Promise<() => void> {
    let isPolling = true;
    let pollCount = 0;
    const startTime = Date.now();

    const poll = async () => {
      if (!isPolling) return;
      pollCount++;

      try {
        const session = await this.getAnalysisSession(sessionId);
        
        if (!session) {
          const debugContext = createDebugContext(sessionId, 'unknown', 'POLLING');
          AIAnalysisDebugger.log(debugContext, 'error', 'Analysis session not found during polling');
          onError('Analysis session not found');
          return;
        }

        // Enhanced progress logging
        const debugContext = createDebugContext(sessionId, session.symbol, 'POLLING');
        AIAnalysisDebugger.log(debugContext, 'info', 
          `Poll #${pollCount}: ${session.status} (${session.progress_percentage}%)`, {
            currentStep: session.current_step,
            totalSteps: session.total_steps,
            elapsedTime: `${Math.round((Date.now() - startTime) / 1000)}s`
          });

        // Log completed steps for debugging
        if (session.card_results) {
          const completedSteps = Object.entries(session.card_results)
            .filter(([_, result]) => result.status === 'completed')
            .map(([stepName, _]) => stepName);
          
          if (completedSteps.length > 0) {
            AIAnalysisDebugger.log(debugContext, 'info', 
              `Completed steps: ${completedSteps.join(', ')}`);
          }

          // Monitor quality for completed trends analysis
          if (session.card_results.trends_analysis?.status === 'completed' && 
              session.card_results.trends_analysis?.data) {
            await this.monitorTrendsAnalysisQuality(
              sessionId, 
              session.symbol, 
              session.card_results.trends_analysis.data
            );
          }
        }

        onProgress(session);

        if (session.status === 'COMPLETED') {
          const totalTime = Math.round((Date.now() - startTime) / 1000);
          AIAnalysisDebugger.log(debugContext, 'info', 
            `Analysis completed in ${totalTime}s after ${pollCount} polls`);
          
          // Log final quality metrics
          await this.logFinalAnalysisMetrics(session);
          
          onComplete(session);
          isPolling = false;
        } else if (session.status === 'FAILED') {
          AIAnalysisDebugger.log(debugContext, 'error', 
            `Analysis failed: ${session.error_message}`, {
              pollCount,
              elapsedTime: `${Math.round((Date.now() - startTime) / 1000)}s`
            });
          onError(session.error_message || 'Analysis failed');
          isPolling = false;
        } else if (session.status === 'RATE_LIMITED') {
          AIAnalysisDebugger.log(debugContext, 'warn', 
            `Rate limited, will retry after: ${session.retry_after}`);
          // Continue polling with increased interval for rate limits
          setTimeout(poll, Math.max(intervalMs * 2, 5000));
          return;
        } else {
          // Continue polling
          setTimeout(poll, intervalMs);
        }
      } catch (error) {
        const debugContext = createDebugContext(sessionId, 'unknown', 'POLLING');
        AIAnalysisDebugger.log(debugContext, 'error', 
          'Polling error occurred', { error: error instanceof Error ? error.message : 'Unknown error' });
        onError(error instanceof Error ? error.message : 'Polling error');
        isPolling = false;
      }
    };

    // Start polling
    poll();

    // Return stop function
    return () => {
      isPolling = false;
      const debugContext = createDebugContext(sessionId, 'unknown', 'POLLING');
      AIAnalysisDebugger.log(debugContext, 'info', `Polling stopped after ${pollCount} polls`);
    };
  }

  /**
   * Monitor trends analysis quality during polling
   */
  private static async monitorTrendsAnalysisQuality(
    sessionId: string, 
    symbol: string, 
    trendsData: any
  ): Promise<void> {
    try {
      const debugContext = createDebugContext(sessionId, symbol, 'TRENDS_ANALYSIS');
      
      // Check for neutral bias issue
      const timeframes = ['short', 'medium', 'long'];
      const neutralCount = timeframes.filter(tf => 
        trendsData[tf]?.direction === 'neutral'
      ).length;

      if (neutralCount === timeframes.length) {
        AIAnalysisDebugger.log(debugContext, 'warn', 
          'All timeframes returned neutral direction - possible AI prompt issue', {
            trendsData: timeframes.map(tf => ({
              timeframe: tf,
              direction: trendsData[tf]?.direction,
              strength: trendsData[tf]?.strength
            }))
          });
      }

      // Check strength vs direction consistency
      timeframes.forEach(tf => {
        const tfData = trendsData[tf];
        if (tfData) {
          if (tfData.direction === 'up' && tfData.strength < 60) {
            AIAnalysisDebugger.log(debugContext, 'warn', 
              `${tf} timeframe: direction="up" but low strength (${tfData.strength}%)`);
          } else if (tfData.direction === 'down' && tfData.strength < 60) {
            AIAnalysisDebugger.log(debugContext, 'warn', 
              `${tf} timeframe: direction="down" but low strength (${tfData.strength}%)`);
          }
        }
      });

    } catch (error) {
      console.error('Error monitoring trends analysis quality:', error);
    }
  }

  /**
   * Log comprehensive analysis metrics when session completes
   */
  private static async logFinalAnalysisMetrics(session: AiAnalysisSession): Promise<void> {
    try {
      const debugContext = createDebugContext(session.id, session.symbol, 'FINAL_METRICS');
      
      // Calculate completion metrics
      const completedSteps = Object.entries(session.card_results)
        .filter(([_, result]) => result.status === 'completed').length;
      const totalSteps = Object.keys(session.card_results).length;
      const completionRate = (completedSteps / totalSteps) * 100;

      AIAnalysisDebugger.log(debugContext, 'info', 
        `Final analysis metrics: ${completedSteps}/${totalSteps} steps completed (${completionRate.toFixed(1)}%)`, {
          sessionDuration: session.completed_at && session.started_at ? 
            `${Math.round((new Date(session.completed_at).getTime() - new Date(session.started_at).getTime()) / 1000)}s` : 'unknown',
          progressPercentage: session.progress_percentage
        });

      // Log specific step statuses
      const stepStatuses = Object.entries(session.card_results).map(([stepName, result]) => ({
        step: stepName,
        status: result.status,
        hasData: !!result.data,
        timestamp: result.timestamp
      }));

      AIAnalysisDebugger.log(debugContext, 'info', 'Step completion details', { stepStatuses });

    } catch (error) {
      console.error('Error logging final analysis metrics:', error);
    }
  }

  /**
   * Get analysis performance metrics
   */
  static async getAnalysisPerformance(sessionId: string) {
    try {
      const { data, error } = await supabase
        .from('ai_analysis_performance')
        .select('*')
        .eq('session_id', sessionId)
        .single();

      if (error) {
        console.error('Error fetching analysis performance:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getAnalysisPerformance:', error);
      return null;
    }
  }
}
